import { capFirstChar, removeOrAddUnderscore } from 'src/helpers/utilities';
import {
	VENTORA_FAN_SOURCE_I18N,
	VENTORA_FAN_SOURCE_ICONS,
} from 'src/components/Scenes/helpers/ventora_fan_scenes_helpers';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

const genTranslation = (title, condition, icons = {}) => {
	return {
		title,
		condition,
		icons: icons || {},
	};
};

const getBoltStateTranslation = (condition) => {
	return genTranslation(
		get(format)(`title_scene_condition_bolt_state`, {
			default: 'When the lock is',
		}),
		get(format)(`condition_operator_bolt_state_${condition}`, {
			default: `${capFirstChar(condition)}`,
		}),
		{
			open: 'fas fa-door-open',
			day_lock: 'fas fa-unlock',
			night_lock: 'fas fa-lock',
		},
	);
};

const getFanSourceTranslation = (source: string) => {
	const sourceI18nKey = VENTORA_FAN_SOURCE_I18N[source];
	const sourceLabel = sourceI18nKey ? get(format)(sourceI18nKey, { default: source }) : source;

	return genTranslation(
		get(format)('scene_condition_fan_source', {
			default: 'When auto source is',
		}),
		sourceLabel,
		VENTORA_FAN_SOURCE_ICONS,
	);
};

const getDoorTranslation = (condition) => {
	return genTranslation(
		get(format)(`title_scene_condition_door`, {
			default: 'When door/window sensor is',
		}),
		get(format)(`condition_operator_door_${condition}`, {
			default: `${capFirstChar(condition)}`,
		}),
		{
			open: 'fas fa-door-open',
			close: 'fas fa-door-closed',
		},
	);
};

const getTurnTranslation = (i18tr, sensorName, condition) => {
	return genTranslation(
		get(format)(sensorName ? `title_scene_condition_sensor_name` : `title_scene_condition`, {
			default: sensorName ? `When "${sensorName}" is` : `When Shelly is`,
			values: { name: sensorName },
		}),
		get(format)(`condition_operator_turn_${condition}`, {
			default: `Turn ${condition}`,
		}),
	);
};

const mappedWhenDetectIcons = {
	vibration: {
		detected: 'fas fa-waveform',
		'not detected': 'fas fa-dash',
	},
	motion: {
		detected: 'fas fa-person-walking',
		'not detected': 'fas fa-person',
	},
	smoke: {
		detected: 'fa-solid fa-fire-smoke',
		'not detected': 'fas fa-sensor',
	},
	flood: {
		detected: 'fas fa-house-flood-water',
		'not detected': 'fas fa-droplet-slash',
	},
	moisture_alarm: {
		detected: 'fas fa-droplet',
		'not detected': 'fas fa-droplet-slash',
	},
	liquid_level: {
		detected: 'fas fa-droplet',
		'not detected': 'fas fa-droplet-slash',
	},
	gas: {
		detected: 'fas fa-siren-on',
		'not detected': 'fas fa-siren',
	},
};

const getWenDetectedTranslation = (i18tr, condition) => {
	if (i18tr === 'vibration') {
		return genTranslation(
			get(format)('title_when_there_is', {
				default: 'When there is',
			}),
			removeOrAddUnderscore(condition, 'addUnderscore') === 'detected'
				? capFirstChar(
						get(format)('vibration_detected', {
							default: 'vibration detected',
						}),
					)
				: capFirstChar(get(format)('no_vibration', { default: 'no vibration' })),
			mappedWhenDetectIcons[i18tr],
		);
	} else if (i18tr === 'liquid_level') {
		return genTranslation(
			get(format)('title_when_there_is', {
				default: 'When there is',
			}),
			get(format)(`condition_operator_liquid_level_${removeOrAddUnderscore(condition, 'addUnderscore')}`, {
				default: `${capFirstChar(condition)} ${i18tr}`,
				values: {
					sensorName: get(format)(`prop_${i18tr}`, { default: `${i18tr}` }),
				},
			}),
			mappedWhenDetectIcons[i18tr],
		);
	}

	return genTranslation(
		get(format)(`title_scene_condition_detected`, {
			default: `When ${i18tr} sensor`,
			values: {
				sensorName: get(format)(`prop_${i18tr}`, { default: `${i18tr}` }),
			},
		}),
		get(format)(`condition_operator_detected_${removeOrAddUnderscore(condition, 'addUnderscore')}`, {
			default: `${capFirstChar(condition)} ${i18tr}`,
			values: {
				sensorName: get(format)(`prop_${i18tr}`, { default: `${i18tr}` }),
			},
		}),
		mappedWhenDetectIcons[i18tr],
	);
};

const getInputValueTranslation = (i18tr, condition, inputValue, unit) => {
	//temperature, humidity, illumination, battery, roller_position, brightness, tilt, total_current, adc, voltage_meter, analog_input_percent, power_meter, distance
	const currentUnit = i18tr === 'temperature' && unit.length === 1 ? '°' + unit : unit;

	return genTranslation(
		get(format)(`title_scene_condition_with_input`, {
			default: `When ${removeOrAddUnderscore(i18tr, 'removeUnderscore')} is`,
			values: { value: get(format)(`prop_${i18tr}`, { default: `${i18tr}` }) },
		}),
		get(format)(
			condition !== 'equal'
				? `condition_operator_${removeOrAddUnderscore(condition, 'addUnderscore')}`
				: 'condition_operator_equal',
			{
				default: `${capFirstChar(condition)} ${inputValue} ${currentUnit}`,
				values: { value: inputValue, units: currentUnit },
			},
		),
		{
			'less than': 'fas fa-arrow-down',
			'more than': 'fas fa-arrow-up',
			equal: 'fas fa-equals',
		},
	);
};

const getCameraBooleanTranslation = (
	titleKey,
	titleDefault,
	trueKey,
	trueDefault,
	falseKey,
	falseDefault,
	condition,
	icons,
) => {
	return genTranslation(
		get(format)(titleKey, { default: titleDefault }),
		condition === 'true'
			? get(format)(trueKey, { default: trueDefault })
			: get(format)(falseKey, { default: falseDefault }),
		icons,
	);
};

const getEqualStateTranslation = (i18tr, sensorName, condition) => {
	const defaultTrans = genTranslation(
		get(format)(`scene_condition_base`, {
			default: `When ${i18tr} is`,
		}),
		get(format)(`condition_operator_base_${condition}`, {
			default: `${capFirstChar(condition)}`,
		}),
	);

	const translations = {
		bolt_state: getBoltStateTranslation(condition),
		fan_on: getTurnTranslation(i18tr, sensorName, condition),
		fan_source: getFanSourceTranslation(condition),
		turn: getTurnTranslation(i18tr, sensorName, condition),
		input_turn: getTurnTranslation(i18tr, sensorName, condition),
		ext_switch: getTurnTranslation(i18tr, sensorName, condition),
		turn_add_on: getTurnTranslation(i18tr, sensorName, condition),
		roller_last_state: genTranslation(
			get(format)('title_scene_condition_roller_state', {
				default: 'When roller is',
			}),
			get(format)(`condition_operator_turn_${condition}`, {
				default: capFirstChar(condition),
			}),
			{
				open: 'fas fa-door-open',
				close: 'fas fa-door-closed',
				stop: 'fas fa-hand',
			},
		),
		roller_direction: genTranslation(
			get(format)('title_scene_condition_roller_direction', {
				default: 'When roller direction',
			}),
			get(format)(`condition_operator_turn_${condition}`, {
				default: capFirstChar(condition),
			}),
			{
				open: 'fas fa-door-open',
				close: 'fas fa-door-closed',
			},
		),
		occupancy: genTranslation(
			get(format)('scene_condition_base', {
				default: 'When {key} is {value}',
				values: {
					key: get(format)('prop_parking_spot', { default: 'the parking spot' }),
					value: condition,
				},
			}),
			get(format)(`label_${condition}`, {
				default: capFirstChar(condition),
			}),
			{
				occupied: 'fas fa-garage-car',
				vacant: 'fas fa-garage-empty',
			},
		),
		opening_state: genTranslation(
			get(format)('title_scene_condition_cover_state', {
				default: 'When device is',
			}),
			get(format)(`condition_operator_cover_${condition}`, {
				default: capFirstChar(condition),
			}),
			{
				open: 'fas fa-blinds-raised',
				opening: 'fas fa-blinds-raised',
				closed: 'fas fa-blinds',
				closing: 'fas fa-blinds',
				stopped: 'fas fa-hand',
			},
		),
		flood: getWenDetectedTranslation(i18tr, condition),
		moisture_alarm: getWenDetectedTranslation(i18tr, condition),
		liquid_level: getWenDetectedTranslation(i18tr, condition),
		gas: getWenDetectedTranslation(i18tr, condition),
		water: getWenDetectedTranslation(i18tr, condition),
		smoke: getWenDetectedTranslation(i18tr, condition),
		motion: getWenDetectedTranslation(i18tr, condition),
		vibration: getWenDetectedTranslation(i18tr, condition),
		luxury: genTranslation(
			get(format)('title_scene_condition_illumination', {
				default: 'When it is',
			}),
			get(format)(`condition_operator_luxury_${condition}`, {
				default: capFirstChar(condition),
			}),
			{
				dark: 'fas fa-moon',
				twilight: 'fas fa-sun-haze',
				bright: 'fas fa-sun',
			},
		),
		input_value: undefined,
		push_type: genTranslation(
			get(format)(`title_scene_condition_push_type`, {
				default: 'When button is pushed',
			}),
			get(format)(`condition_operator_push_type_${condition}`, {
				default: `${capFirstChar(removeOrAddUnderscore(condition, 'removeUnderscore'))}`,
			}),
			{
				short: 'fak fa-solid-short-pushed',
				long: 'fak fa-solid-long-pushed',
				double_short: 'fak fa-solid-double-short-pushed',
				triple_short: 'fak fa-solid-triple-short-pushed',
				short_long: 'fak fa-solid-short-long-pushed',
				long_short: 'fak fa-solid-long-short-pushed',
			},
		),
		'door state': getDoorTranslation(condition),
		door_state: getDoorTranslation(condition),
		valve_open_state: genTranslation(
			get(format)(`title_scene_condition_valve`, {
				default: 'Valve is',
			}),
			get(format)(`condition_operator_valve_${condition}`, {
				default: `${capFirstChar(condition)}`,
			}),
			{
				open: 'fak fa-light-valve',
				closed: 'fak fa-solid-valve',
			},
		),
		remote_control_channel: genTranslation(
			get(format)(`title_scene_condition_channel`, {
				default: 'When remote control channel is:',
			}), //ToDo: add translation keys
			get(format)(`condition_operator_channel_${condition}`, {
				default: `${capFirstChar(condition)}`,
			}),
		),
		scroll_position: genTranslation(
			get(format)(`title_scene_condition_scroll_direction`, {
				default: 'When scroll direction is:',
			}),
			condition === 'L'
				? get(format)('scroll_up', { default: 'Up' })
				: get(format)('scroll_down', { default: 'Down' }),
			{
				L: 'fas fa-arrow-up',
				R: 'fas fa-arrow-down',
			},
		),
		tariff: genTranslation(
			get(format)('title_scene_condition_tariff', {
				default: 'When tariff is',
			}),
			get(format)(`condition_operator_tariff_${condition}`, {
				default: condition,
			}),
			{
				T1: 'fa fa-tags',
				T2: 'fa fa-tags',
				T3: 'fa fa-tags',
				T4: 'fa fa-tags',
			},
		),
		camera_motion: getCameraBooleanTranslation(
			'title_scene_condition_camera_motion',
			'When camera motion is',
			'option_camera_motion',
			'Motion detected',
			'option_camera_motion_end',
			'Motion stopped',
			condition,
			{
				true: 'fas fa-person-walking',
				false: 'fas fa-person',
			},
		),
		camera_privacy: getCameraBooleanTranslation(
			'title_scene_condition_camera_privacy',
			'When privacy mode is',
			'camera_privacy_mode_on',
			'Privacy On',
			'camera_privacy_mode_off',
			'Privacy Off',
			condition,
			{
				true: 'fas fa-video-slash',
				false: 'fas fa-video',
			},
		),
		camera_arm: getCameraBooleanTranslation(
			'title_scene_condition_camera_arm',
			'When camera is',
			'camera_armed',
			'Armed',
			'camera_disarmed',
			'Disarmed',
			condition,
			{
				true: 'fas fa-shield',
				false: 'fas fa-shield-slash',
			},
		),
		camera_zone_motion: getCameraBooleanTranslation(
			'title_scene_condition_camera_zone_motion',
			'When zone motion is',
			'option_camerazone_motion',
			'Motion detected in zone',
			'option_camerazone_motion_end',
			'Motion stopped in zone',
			condition,
			{
				true: 'fas fa-person-walking',
				false: 'fas fa-person',
			},
		),
	};
	return translations[i18tr] || defaultTrans;
};

export const getDevicePropTranslation = (
	i18tr,
	sensorName = '',
	inputValue = undefined,
	condition = '',
	unit = '',
) => {
	return typeof inputValue === 'undefined'
		? getEqualStateTranslation(i18tr, sensorName, condition)
		: getInputValueTranslation(i18tr, condition, inputValue, unit);
};
